import React, { useState } from 'react';
import styled from 'styled-components';
import { cx } from '../../../utils/cx';
import decodeHTMLEntities from '../../../utils/decode-html-entities';
import { formatPrice } from '../../../utils/format-number';
import { getRoomTypes } from '../../../utils/hotels.js';
import { translateFromMap } from '../../../utils/i18n.js';
import { getImage } from '../../../utils/images';
import { translate } from '../../../utils/wp.js';
import { HotelInfoDialog } from '../../HotelInfoDialog';
import { Rating } from '../../Rating';
import { HotelPackageTag } from './HotelPackageTag';

function SingleRoomAddonTag({
    numberOfPersons,
    selectedRoomType,
    singleRoomPrice,
    price,
}) {
    const addon = singleRoomPrice - price;
    if (
        numberOfPersons % 2 !== 1 ||
        selectedRoomType !== 0 ||
        addon === 0
    ) {
        return null;
    }

    return (
        <div className="flex items-center gap-x-2">
            <span className="font-medium">{translate('single_room_addon')}</span>
            <strong>+ {formatPrice(singleRoomPrice - price)} SEK</strong>
        </div>
    );
}

function TripleRoomAddonTag({
    numberOfPersons,
    selectedRoomType,
    tripleRoomPrice,
    price,
}) {
    const addon = ((tripleRoomPrice - price) * 3);

    if (
        numberOfPersons % 2 !== 1 ||
        selectedRoomType !== 1 ||
        addon <= 0
    ) {
        return null;
    }

    return (
        <div className="flex items-center gap-x-2">
            <span className="font-medium">{translate('triple_room_addon')}</span>
            <strong>+ {formatPrice(addon)} SEK</strong>
        </div>
    );
}

export function HotelListItem({
    hotel,
    disabled,
    price,
    priceSingle,
    priceTripple,
    available,
    includedPrice,
    selectedHotelRoomType,
    confirmedTickets,
    onSelect,
}) {
    const [showHotelInfo, setShowHotelInfo] = useState(false);
    const [hotelInfoSection, setHotelInfoSection] = useState('about');

    const image = getImage(
        hotel.image_meta,
        [
            'woocommerce_single',
            'post-image',
            'group-image',
            'shop_single',
            'woocommerce_thumbnail',
            'thumbnail',
        ],
    );

    const specialHotelInfo = application
        .product
        .special_hotels
        .find(h => h.id === hotel.id);

    function onClickShowHotelInfoDrawer(e) {
        e.stopPropagation();

        setHotelInfoSection('about');
        setShowHotelInfo(true);
    }

    const classNames = cx(
        'hotel box relative p-0 mb-2 bg-white rounded flex flex-col flex-wrap gap-x-4 sm:flex-row sm:p-4',
        !available && 'hotel--outofstock',
        disabled && 'hotel--disabled',
        specialHotelInfo && 'hotel--special',
    );

    const roomTypes = getRoomTypes(
        confirmedTickets.length,
        hotel.room_types,
        selectedHotelRoomType,
    );

    return (
        <>
            <div className={classNames} data-cy="hotel">
                <div className="hotel__image relative rounded-t overflow-hidden h-[140px] sm:w-[240px] sm:h-auto sm:rounded">
                    <img
                        className="hotel__image__viewport absolute block inset-0 h-full w-full object-cover object-center"
                        src={image}
                    />
                    <div className="absolute top-2 left-2 flex gap-x-1">
                        {price - includedPrice === 0 && available && (
                            <div className="bg-tag px-2 rounded-sm font-semibold text-[12px] text-primarytext leading-[20px]">
                                {translate('included')}
                            </div>
                        )}
                        {specialHotelInfo &&
                            <SpecialTag className={`special-tag unavailable bg-tag px-2 rounded-sm font-semibold text-[12px] leading-[20px] ${specialHotelInfo.bg_color}`}>
                                {translateFromMap({
                                    se: specialHotelInfo.tag,
                                    en: specialHotelInfo.tag_en,
                                })}
                            </SpecialTag>
                        }
                    </div>

                </div>
                <div className="hotel__info flex-1 relative p-4 text-xs sm:p-0">
                    <Rating rating={hotel.stars} />
                    <div
                        className="text-lg font-bold"
                        translate="no"
                    >
                        {decodeHTMLEntities(hotel.name)}
                    </div>
                    <div
                        className="text-[11px] font-medium opacity-50"
                        translate="no"
                    >
                        {hotel.address}
                    </div>

                    <div className="flex gap-2 mt-2">
                        {roomTypes.map(rt => (
                            <div
                                key={rt.type}
                                className="flex items-center gap-x-1.5 px-1.5 rounded-sm bg-lightblack text-white text-[11px] leading-[20px] font-semibold"
                            >
                                <i className="icon-bed text-[16px]"></i>
                                <span>{rt.count} x {rt.label}</span>
                            </div>
                        ))}
                    </div>

                    {hotel.usps && hotel.usps.length > 0 &&
                        <div className="py-3 mt-4 border-y border-black/10 flex flex-wrap items-center gap-3">
                            {hotel.usps.map((usp, i) => (
                                <div
                                    key={i}
                                    className="flex items-center gap-x-1"
                                >
                                    <i className="icon-check-outline text-[16px] text-green"></i>
                                    <span>{usp}</span>
                                </div>
                            ))}
                        </div>
                    }

                    <div className="py-3 mb-4 border-b border-black/10 flex items-center gap-x-4">
                        <HotelPackageTag
                            price={price}
                            includedPrice={includedPrice}
                            available={available}
                        />
                        <SingleRoomAddonTag
                            numberOfPersons={confirmedTickets.length}
                            selectedRoomType={selectedHotelRoomType}
                            singleRoomPrice={priceSingle}
                            price={price}
                        />
                        <TripleRoomAddonTag
                            numberOfPersons={confirmedTickets.length}
                            selectedRoomType={selectedHotelRoomType}
                            tripleRoomPrice={priceTripple}
                            price={price}
                        />
                    </div>
                    <div className="flex items-center gap-x-4">
                        <button
                            className="inline-flex items-center gap-x-2 rounded-full px-4 py-2.5 text-sm font-medium border border-primary"
                            onClick={onClickShowHotelInfoDrawer}
                            data-cy="show-info-about-hotel"
                        >
                            <i className="icon-info text-[16px]"></i>
                            {translate('information')}
                        </button>
                        {available && (
                            <button
                                className="inline-flex items-center gap-x-2 rounded-full px-6 py-2.5 text-sm font-semibold bg-primary border border-primary text-primarytext"
                                onClick={() => onSelect(hotel)}
                                data-cy="select-hotel"
                            >
                                {window.application.product.use_alternative_step_texts
                                    ? translate('choose')
                                    : translate('choose_hotel')}
                            </button>
                        )}

                        {!available && (
                            <button
                                className="inline-flex items-center gap-x-2 rounded-full px-6 py-2.5 text-sm font-semibold bg-disabled border border-disabled cursor-not-allowed pointer-events-none"
                            >
                                {translate('fully_booked')}
                            </button>
                        )}
                    </div>
                </div>
            </div>
            <HotelInfoDialog
                visible={showHotelInfo}
                section={hotelInfoSection}
                hotel={hotel}
                tickets={confirmedTickets}
                onToggleSection={section => setHotelInfoSection(section)}
                onClose={() => setShowHotelInfo(false)}
            />
        </>
    );
}

const SpecialTag = styled.div`
    &.black {
        background: #140f1f;
        color: white;
    }

    &.orange {
        background: #ed8d64;
        color: white;
    }

    &.blue {
        background: #4c639f;
        color: white;
    }

    &.middleblue {
        background: #3c83a0;
        color: white;
    }

    &.turkos {
        background: #4ac8c0;
        color: white;
    }

    &.purple {
        background: #674c9f;
        color: white;
    }

    &.lightpurple {
        background: #a570dc;
        color: white;
    }

    &.pink {
        background: #dc70c4;
        color: white;
    }

    &.cerise {
        background: #e51f7c;
        color: white;
    }

    &.white {
        background: white;
        color: black;
    }

    &.green {
        background: #6bb071;
        color: white;
    }

    &.yellow {
        background: #f2ab49;
        color: white;
    }
`;
